import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { ActivityService } from '../../../user/activity/activity.service';
import { MovimientoTarjetaCredito, PaymentCardModel } from '../paymentcard.model';
import { PaymentCardService } from '../paymentcard.service';
import { MasterService } from '../../../../modules_v2/master/services/master.service';
import { NotificationService } from '../../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../../modules_v2/shared/components/confirm-dialog.component';
import { ActualSelected, Utils } from '../../../../shared/utils';
import { consts } from '../../../../consts';
import {
  currentActivityOption,
  EntityFormOption,
  fromApiDate,
  InjectedOptions,
  optionId,
} from '../../../../modules_v2/finance/entity-form/entity-form.utils';

export interface PaymentCardMovementDialogData {
  paymentCard: PaymentCardModel;
  tipoMovimientoTarjetaCreditoId: number;
  editUid?: string;
}

@Component({
    selector: 'convfact-paymentcard-movimiento-tarjeta-credito',
    templateUrl: './paymentcard-dialog-movimiento-tarjeta-credito.component.html',
    styleUrls: ['./paymentcard-dialog-movimiento-tarjeta-credito.component.css'],
    standalone: false
})
export class PaymentCardDialogMovimientoTarjetaCreditoComponent implements OnInit, OnDestroy {

  public paymentCard: PaymentCardModel = this.data.paymentCard;

  public tipoMovimientoTarjetaCreditoId: number = this.data.tipoMovimientoTarjetaCreditoId;

  public tittle: string = 'Elemento';

  public ready: boolean = false;

  public listLoading: boolean = false;

  public loadError: boolean = false;

  public saving: boolean = false;

  public submitted: boolean = false;

  public saveError: string = '';

  public isEditMode: boolean = false;
  public editingUid: string = '';

  public activities: EntityFormOption[] = [];

  public dataSource: MovimientoTarjetaCredito[] = [];

  public readonly year = ActualSelected.yearSelected;

  public projects: EntityFormOption[] = [];
  public expenseProjectTypes: EntityFormOption[] = [];
  public isProjectEnable: boolean = false;

  public form: UntypedFormGroup;

  private model: MovimientoTarjetaCredito = this.emptyMovement();
  private changesAtModal: boolean = false;
  private requestedEditDone: boolean = false;
  private readonly injected = new InjectedOptions();
  private readonly destroy$ = new Subject<void>();

  static open(dialog: MatDialog, data: PaymentCardMovementDialogData): Observable<boolean> {
    return dialog.open<PaymentCardDialogMovimientoTarjetaCreditoComponent, PaymentCardMovementDialogData, boolean>(PaymentCardDialogMovimientoTarjetaCreditoComponent, {
      width: '880px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<PaymentCardDialogMovimientoTarjetaCreditoComponent, boolean>,
    public paymentCardService: PaymentCardService,
    public activityService: ActivityService,
    @Inject(MAT_DIALOG_DATA) public data: PaymentCardMovementDialogData,
    private masterService: MasterService,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      fecha: [new Date()],
      concepto: [''],
      importe: [null],
      comisiones: [0],
      intereses: [0],
      activity: [null],
      project: [null],
      expenseProjectType: [null],
    });
  }

  get isDisposicion(): boolean {
    return this.tipoMovimientoTarjetaCreditoId === 1;
  }

  get cardSubtitle(): string {
    const number = (this.paymentCard && this.paymentCard.number) || '';
    const name = (this.paymentCard && this.paymentCard.name) || '';
    return [name, number.replace(/\d{4}(?= \d{4})/g, '****'), this.year ? 'Ejercicio ' + this.year : '']
      .filter(part => !!part)
      .join(' · ');
  }

  get conceptoLength(): number {
    return String(this.form.get('concepto').value || '').length;
  }

  get missingFecha(): boolean {
    return !this.form.get('fecha').value;
  }

  get missingConcepto(): boolean {
    return !String(this.form.get('concepto').value || '').trim();
  }

  get invalidImporte(): boolean {
    const importe = this.form.get('importe').value;
    return !importe || Number(importe) === 0 || isNaN(Number(importe));
  }

  get invalidComisiones(): boolean {
    const value = this.form.get('comisiones').value;
    return value === null || value === undefined || value === '';
  }

  get invalidIntereses(): boolean {
    const value = this.form.get('intereses').value;
    return value === null || value === undefined || value === '';
  }

  get missingActivity(): boolean {
    return optionId(this.form.get('activity').value) === null;
  }

  get hasErrors(): boolean {
    return this.missingFecha || this.missingConcepto || this.invalidImporte || this.invalidComisiones || this.invalidIntereses || this.missingActivity;
  }

  get totalImporte(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }

  public control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  public ngOnInit(): void {
    this.dialogRef.backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.closeDialog());
    this.dialogRef.keydownEvents()
      .pipe(filter(event => event.key === 'Escape'), takeUntil(this.destroy$))
      .subscribe(() => this.closeDialog());

    if (this.tipoMovimientoTarjetaCreditoId === 1) {
      this.tittle = 'Disposición';
    } else if (this.tipoMovimientoTarjetaCreditoId === 2) {
      this.tittle = 'Liquidación';
    }

    this.load();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public retry(): void {
    this.loadError = false;
    this.load();
  }

  public save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.hasErrors) return;

    const editing = this.isEditMode;
    const payload = this.buildPayload();

    this.saving = true;

    const request = editing
      ? this.paymentCardService.movimientoTarjetaCreditoUpdate(payload)
      : this.paymentCardService.movimientoTarjetaCreditoCreate(payload);

    request
      .then(() => {
        this.changesAtModal = true;
        this.saving = false;
        this.notify.ok(`${this.tittle} ${editing ? 'actualizada' : 'añadida'} correctamente`);
        if (!editing || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.resetForm();
        this.reloadList();
      })
      .catch(error => {
        this.saving = false;
        this.saveError = (error && error.error && error.error.message) || 'Ha ocurrido un error al guardar el movimiento';
      });
  }

  public delete(item: MovimientoTarjetaCredito): void {
    if (this.saving) return;
    this.confirm.open({
      title: `Eliminar ${this.tittle.toLowerCase()}`,
      subtitle: (this.paymentCard && this.paymentCard.name) || undefined,
      message: 'Se eliminará el movimiento de la tarjeta junto con su asiento contable. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.movementDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando movimiento…',
      errorText: 'Ha ocurrido un error al eliminar',
      action: () => this.paymentCardService.movimientoTarjetaCreditoDelete(item.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changesAtModal = true;
      this.notify.ok(`${this.tittle} eliminada correctamente`);
      if (this.isEditMode && this.editingUid === item.uid) this.resetForm();
      this.reloadList();
    });
  }

  public edit(item: MovimientoTarjetaCredito): void {
    if (this.saving) return;
    const elementSearched = this.dataSource.find(f => f.uid === item.uid);
    if (!elementSearched) return;

    this.isEditMode = true;
    this.editingUid = elementSearched.uid;
    this.submitted = false;
    this.saveError = '';
    this.injected.clear();

    this.model = Object.assign(this.emptyMovement(), elementSearched, {
      projectUid: elementSearched.projectUid || '',
      expenseProjectTypeUid: elementSearched.expenseProjectTypeUid || '',
    });

    this.form.patchValue({
      fecha: fromApiDate(elementSearched.fecha),
      concepto: elementSearched.concepto || '',
      importe: elementSearched.importe != null ? Number(elementSearched.importe) : null,
      comisiones: elementSearched.comisiones != null ? Number(elementSearched.comisiones) : 0,
      intereses: elementSearched.intereses != null ? Number(elementSearched.intereses) : 0,
      activity: this.injected.ensure(this.activities, elementSearched.activitiesUid, elementSearched.activitiesName),
      project: this.injected.ensure(this.projects, elementSearched.projectUid),
      expenseProjectType: this.injected.ensure(this.expenseProjectTypes, elementSearched.expenseProjectTypeUid),
    }, { emitEvent: false });
  }

  public cancelEdit(): void {
    this.resetForm();
  }

  public closeDialog(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changesAtModal);
  }

  public trackByUid(_: number, item: MovimientoTarjetaCredito): string {
    return item.uid;
  }

  private load(): void {
    this.ready = false;
    Promise.all([
      this.loadActivities(),
      this.loadProjectLookups(),
      this.listRequest(),
    ])
      .then(([activities, projectLookups, list]) => {
        this.activities = activities;
        this.isProjectEnable = projectLookups.enabled;
        this.projects = projectLookups.projects;
        this.expenseProjectTypes = projectLookups.expenseProjectTypes;
        this.dataSource = list;
        this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private reloadList(): void {
    this.listLoading = true;
    this.listRequest()
      .then(list => {
        this.dataSource = list;
        this.openRequestedEdit();
      })
      .catch(() => { this.notify.error('No se pudieron recargar los movimientos'); })
      .then(() => { this.listLoading = false; });
  }

  private listRequest(): Promise<MovimientoTarjetaCredito[]> {
    return this.paymentCardService
      .movimientoTarjetaCreditoList(this.paymentCard.uid, this.tipoMovimientoTarjetaCreditoId)
      .then(response => Array.isArray(response) ? response : []);
  }

  private loadActivities(): Promise<EntityFormOption[]> {
    return this.activityService.getList()
      .then(list => (list || [])
        .filter(activity => activity && activity.uid && activity.uid !== consts.EMPTY_GUID)
        .map(activity => ({ id: activity.uid, display: activity.name || '' })))
      .catch(() => [] as EntityFormOption[]);
  }

  private loadProjectLookups(): Promise<{ enabled: boolean; projects: EntityFormOption[]; expenseProjectTypes: EntityFormOption[] }> {
    const empty = { enabled: false, projects: [] as EntityFormOption[], expenseProjectTypes: [] as EntityFormOption[] };
    return this.activityService.loadData(ActualSelected.activitySelected)
      .then(activity => !!(activity && activity.isProject))
      .catch(() => false)
      .then(enabled => {
        if (!enabled) return empty;
        return Promise.all([
          firstValueFrom(this.masterService.getProjects()).catch(() => []),
          firstValueFrom(this.masterService.getExpenseProjectTypes()).catch(() => []),
        ]).then(([projectList, typeList]) => ({
          enabled,
          projects: (projectList || []).map(project => ({ id: project.id, display: project.display || '' })),
          expenseProjectTypes: (typeList || []).map(type => ({ id: type.id, display: type.display || '' })),
        }));
      });
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const item = this.dataSource.find(i => i.uid === this.data.editUid);
    if (item) this.edit(item);
  }

  private resetForm(): void {
    this.isEditMode = false;
    this.editingUid = '';
    this.injected.clear();
    this.submitted = false;
    this.saveError = '';
    this.model = this.emptyMovement();
    this.form.reset({
      fecha: new Date(),
      concepto: '',
      importe: null,
      comisiones: 0,
      intereses: 0,
      activity: null,
      project: null,
      expenseProjectType: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private applySingleOptionDefaults(): void {
    if (this.isEditMode) return;
    if (!this.missingActivity) return;

    const current = currentActivityOption(this.activities);
    if (current) this.control('activity').setValue(current, { emitEvent: false });
    else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
  }

  private buildPayload(): MovimientoTarjetaCredito {
    const value = this.form.getRawValue();
    return {
      ...this.model,
      paymentCardsUid: this.paymentCard.uid,
      tiposMovimientoTarjetaCreditoId: this.model.tiposMovimientoTarjetaCreditoId || this.tipoMovimientoTarjetaCreditoId,
      fecha: value.fecha,
      concepto: String(value.concepto || '').trim(),
      importe: Number(value.importe) || 0,
      comisiones: Number(value.comisiones) || 0,
      intereses: Number(value.intereses) || 0,
      activitiesUid: optionId(value.activity),
      projectUid: optionId(value.project) || '',
      expenseProjectTypeUid: optionId(value.expenseProjectType) || '',
    };
  }

  private movementDetails(item: MovimientoTarjetaCredito): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (item.fecha) details.push({ label: 'Fecha', value: new Date(item.fecha).toLocaleDateString('es-ES') });
    if (item.concepto) details.push({ label: 'Concepto', value: item.concepto });
    details.push({ label: 'Importe', value: Utils.FormatMoney(Number(item.importe) || 0) + ' €' });
    return details;
  }

  private emptyMovement(): MovimientoTarjetaCredito {
    return {
      uid: '',
      tiposMovimientoTarjetaCreditoId: this.data.tipoMovimientoTarjetaCreditoId,
      activitiesName: '',
      activitiesUid: null,
      bankAccountsId: null,
      fecha: new Date(),
      comisiones: 0,
      concepto: '',
      importe: 0,
      cuentaBancariaAsociadaInfo: '',
      intereses: 0,
      paymentCardsUid: '',
      projectUid: '',
      expenseProjectTypeUid: ''
    };
  }
}
